import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../context/PartnerAuthContext';
import {
  LayoutDashboard, Package, User, Palette, CreditCard, LogOut,
  Star, ShoppingCart, TrendingUp, Box, Save, Loader2,
  ExternalLink, Search, ChevronLeft, ChevronRight, Eye, Plus,
  Trash2, AlertTriangle, Lock, ArrowUpRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnerDashboardPage = () => {
  const navigate = useNavigate();
  const { partner, token, logout, loading: authLoading } = usePartnerAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !partner) navigate('/parceiro/login');
  }, [partner, authLoading, navigate]);

  useEffect(() => {
    if (token) fetchDashboard();
  }, [token]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/partner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setDashData(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0f172a' }}>
        <Loader2 className="w-10 h-10 animate-spin text-green-500" />
      </div>
    );
  }
  if (!partner) return null;

  const tabs = [
    { id: 'overview', label: 'Visao Geral', icon: LayoutDashboard },
    { id: 'products', label: 'Produtos', icon: Package },
    { id: 'profile', label: 'Perfil', icon: User },
    { id: 'site', label: 'Meu Site', icon: Palette },
    { id: 'plan', label: 'Plano', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: '#0f172a' }}>
      <aside className="w-64 border-r border-white/10 flex flex-col" style={{ background: '#0d1525' }}>
        <div className="p-6 border-b border-white/10">
          <h2 className="text-lg font-bold text-white" data-testid="dashboard-title">Painel do Parceiro</h2>
          <p className="text-white/50 text-sm mt-1 truncate">{partner.full_name || partner.email}</p>
          {dashData?.plan && (
            <span className="inline-block mt-2 text-xs font-medium px-2.5 py-1 rounded-full bg-green-500/20 text-green-400">
              {dashData.plan.name}
            </span>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id ? 'bg-green-500/20 text-green-400' : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-5 h-5" /> {tab.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={() => { logout(); navigate('/parceiro/login'); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" /> Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto p-8">
        {activeTab === 'overview' && <OverviewTab data={dashData} />}
        {activeTab === 'products' && <ProductsTab token={token} plan={dashData?.plan} stats={dashData?.stats} onUpdate={fetchDashboard} />}
        {activeTab === 'profile' && <ProfileTab data={dashData} token={token} onUpdate={fetchDashboard} />}
        {activeTab === 'site' && <SiteTab data={dashData} token={token} onUpdate={fetchDashboard} />}
        {activeTab === 'plan' && <PlanTab data={dashData} />}
      </main>
    </div>
  );
};

/* ====== OVERVIEW TAB ====== */
const OverviewTab = ({ data }) => {
  if (!data) return null;
  const { stats, profile, plan } = data;
  const maxProd = plan?.max_products === -1 ? 'Ilimitado' : plan?.max_products;
  const usagePercent = plan?.max_products === -1 ? 0 : Math.round((stats.total_products / (plan?.max_products || 15)) * 100);

  const cards = [
    { label: 'Produtos', value: stats.total_products, icon: Package, color: '#3b82f6' },
    { label: 'Em Estoque', value: stats.products_in_stock, icon: Box, color: '#22c55e' },
    { label: 'Avaliacao', value: stats.average_rating.toFixed(1), icon: Star, color: '#f59e0b' },
    { label: 'Vendas', value: stats.total_sales, icon: ShoppingCart, color: '#8b5cf6' },
    { label: 'Avaliacoes', value: stats.total_reviews, icon: TrendingUp, color: '#ec4899' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6" data-testid="overview-title">Visao Geral</h1>

      {/* Usage Bar */}
      {plan?.max_products !== -1 && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-white/60">Produtos utilizados</span>
            <span className="text-white font-medium">{stats.total_products} / {maxProd}</span>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2.5">
            <div className={`h-2.5 rounded-full transition-all ${usagePercent > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(usagePercent, 100)}%` }} />
          </div>
          {usagePercent > 80 && (
            <p className="text-amber-400 text-xs mt-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Proximo do limite. Considere fazer upgrade.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${card.color}20` }}>
                  <Icon className="w-5 h-5" style={{ color: card.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{card.value}</p>
              <p className="text-white/50 text-sm">{card.label}</p>
            </div>
          );
        })}
      </div>

      {profile && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Seu Site Publico</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-white/60 text-sm mb-1">URL do seu site:</p>
              <code className="text-green-400 text-sm bg-white/5 px-3 py-1.5 rounded-lg" data-testid="site-url">/parceiros/{profile.slug}</code>
            </div>
            <a href={`/parceiros/${profile.slug}`} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-colors text-sm font-medium"
              data-testid="visit-site-btn"
            >
              <ExternalLink className="w-4 h-4" /> Visitar Site
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

/* ====== PRODUCTS TAB ====== */
const ProductsTab = ({ token, plan, stats, onUpdate }) => {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', regular_price: 0, sku: '', stock_quantity: 0, in_stock: true });
  const [saving, setSaving] = useState(false);
  const limit = 20;

  const maxProducts = plan?.max_products || 15;
  const isUnlimited = maxProducts === -1;
  const canAddMore = isUnlimited || (stats?.total_products || 0) < maxProducts;

  useEffect(() => { fetchProducts(); }, [page, search]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit, offset: page * limit });
      if (search) params.set('q', search);
      const res = await fetch(`${API_URL}/api/partner/products?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { const d = await res.json(); setProducts(d.products); setTotal(d.total); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAddProduct = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/partner/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newProduct),
      });
      if (res.ok) {
        setShowAddForm(false);
        setNewProduct({ name: '', regular_price: 0, sku: '', stock_quantity: 0, in_stock: true });
        fetchProducts();
        onUpdate();
      } else {
        const err = await res.json();
        alert(err.detail);
      }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Remover este produto?')) return;
    try {
      const res = await fetch(`${API_URL}/api/partner/products/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { fetchProducts(); onUpdate(); }
    } catch (err) { console.error(err); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white" data-testid="products-title">Meus Produtos</h1>
          <p className="text-white/50 text-sm mt-1">
            {total} de {isUnlimited ? 'ilimitado' : maxProducts} produtos
          </p>
        </div>
        <button onClick={() => canAddMore ? setShowAddForm(true) : alert(`Limite de ${maxProducts} produtos atingido. Faca upgrade!`)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
            canAddMore ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
          data-testid="add-product-btn"
        >
          {canAddMore ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
          Adicionar Produto
        </button>
      </div>

      {/* Add Product Form */}
      {showAddForm && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
          <h3 className="text-white font-semibold mb-4">Novo Produto</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-white/60 text-sm mb-1">Nome do produto *</label>
              <input value={newProduct.name} onChange={e => setNewProduct({...newProduct, name: e.target.value})}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="new-product-name" />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-1">Preco (R$)</label>
              <input type="number" step="0.01" value={newProduct.regular_price} onChange={e => setNewProduct({...newProduct, regular_price: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="new-product-price" />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-1">SKU/Codigo</label>
              <input value={newProduct.sku} onChange={e => setNewProduct({...newProduct, sku: e.target.value})}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="new-product-sku" />
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-1">Estoque</label>
              <input type="number" value={newProduct.stock_quantity} onChange={e => setNewProduct({...newProduct, stock_quantity: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="new-product-stock" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={handleAddProduct} disabled={saving || !newProduct.name}
              className="px-5 py-2.5 bg-green-500 text-white rounded-xl font-medium disabled:opacity-50" data-testid="save-new-product">
              {saving ? 'Salvando...' : 'Salvar Produto'}
            </button>
            <button onClick={() => setShowAddForm(false)} className="px-5 py-2.5 bg-white/10 text-white/60 rounded-xl">Cancelar</button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          placeholder="Buscar produtos..." data-testid="product-search-input"
          className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-green-500" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-green-500" /></div>
      ) : (
        <>
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-4 py-3 text-white/60 text-sm font-medium">Produto</th>
                  <th className="text-left px-4 py-3 text-white/60 text-sm font-medium">SKU</th>
                  <th className="text-right px-4 py-3 text-white/60 text-sm font-medium">Preco</th>
                  <th className="text-right px-4 py-3 text-white/60 text-sm font-medium">Estoque</th>
                  <th className="text-center px-4 py-3 text-white/60 text-sm font-medium">Status</th>
                  <th className="text-center px-4 py-3 text-white/60 text-sm font-medium">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-white/30" />}
                        </div>
                        <span className="text-white text-sm font-medium truncate max-w-[200px]">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/50 text-sm">{p.sku}</td>
                    <td className="px-4 py-3 text-right text-white text-sm font-medium">
                      {(p.sale_price || p.regular_price) > 0 ? `R$ ${(p.sale_price || p.regular_price).toFixed(2)}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-right text-white/60 text-sm">{p.stock_quantity}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${p.in_stock ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {p.in_stock ? 'Estoque' : 'Esgotado'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleDeleteProduct(p.id)}
                        className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        data-testid={`delete-product-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
                className="flex items-center gap-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white/60 hover:text-white disabled:opacity-30 text-sm" data-testid="prev-page-btn">
                <ChevronLeft className="w-4 h-4" /> Anterior
              </button>
              <span className="text-white/50 text-sm">Pagina {page + 1} de {totalPages}</span>
              <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
                className="flex items-center gap-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white/60 hover:text-white disabled:opacity-30 text-sm" data-testid="next-page-btn">
                Proximo <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ====== PROFILE TAB ====== */
const ProfileTab = ({ data, token, onUpdate }) => {
  const [form, setForm] = useState({ name: '', about: '', phone: '', whatsapp: '', address: '', city: '', state: '', website: '', business_hours: '', services: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.profile) {
      const p = data.profile;
      setForm({ name: p.name || '', about: p.about || '', phone: p.phone || '', whatsapp: p.whatsapp || '', address: p.address || '', city: p.city || '', state: p.state || '', website: p.website || '', business_hours: p.business_hours || '', services: (p.services || []).join(', ') });
    }
  }, [data]);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      const payload = { ...form, services: form.services.split(',').map(s => s.trim()).filter(Boolean) };
      await fetch(`${API_URL}/api/partner/profile`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(payload) });
      setSaved(true); onUpdate();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const fields = [
    { key: 'name', label: 'Nome da Empresa', type: 'text' },
    { key: 'about', label: 'Sobre a Empresa', type: 'textarea' },
    { key: 'phone', label: 'Telefone', type: 'text' },
    { key: 'whatsapp', label: 'WhatsApp', type: 'text' },
    { key: 'address', label: 'Endereco', type: 'text' },
    { key: 'city', label: 'Cidade', type: 'text' },
    { key: 'state', label: 'Estado', type: 'text' },
    { key: 'website', label: 'Website', type: 'text' },
    { key: 'business_hours', label: 'Horario de Funcionamento', type: 'text' },
    { key: 'services', label: 'Servicos (separados por virgula)', type: 'text' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white" data-testid="profile-title">Meu Perfil</h1>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-white transition-all disabled:opacity-50"
          style={{ background: saved ? '#22c55e' : 'linear-gradient(135deg, #22c55e, #16a34a)' }}
          data-testid="save-profile-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saved ? 'Salvo!' : saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-5">
        {fields.map((field) => (
          <div key={field.key}>
            <label className="block text-white/70 text-sm font-medium mb-1.5">{field.label}</label>
            {field.type === 'textarea' ? (
              <textarea value={form[field.key]} onChange={e => setForm({ ...form, [field.key]: e.target.value })} rows={4}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                data-testid={`profile-${field.key}-input`} />
            ) : (
              <input value={form[field.key]} onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid={`profile-${field.key}-input`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ====== SITE TAB ====== */
const COLOR_PALETTE = [
  { id: 'green', label: 'Verde', primary: '#22c55e', secondary: '#16a34a' },
  { id: 'blue', label: 'Azul', primary: '#3b82f6', secondary: '#2563eb' },
  { id: 'red', label: 'Vermelho', primary: '#ef4444', secondary: '#dc2626' },
  { id: 'purple', label: 'Roxo', primary: '#8b5cf6', secondary: '#7c3aed' },
  { id: 'amber', label: 'Dourado', primary: '#f59e0b', secondary: '#d97706' },
  { id: 'teal', label: 'Teal', primary: '#14b8a6', secondary: '#0d9488' },
  { id: 'pink', label: 'Rosa', primary: '#ec4899', secondary: '#db2777' },
  { id: 'slate', label: 'Cinza', primary: '#64748b', secondary: '#475569' },
];

const SiteTab = ({ data, token, onUpdate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(data?.profile?.template || 'classic');
  const [selectedColor, setSelectedColor] = useState(data?.profile?.site_colors?.primary || 'green');
  const [saving, setSaving] = useState(false);

  const plan = data?.plan;
  const maxTemplates = plan?.max_templates === -1 ? 999 : (plan?.max_templates || 1);
  const maxColors = plan?.max_colors === -1 ? 999 : (plan?.max_colors || 3);

  const templates = [
    { id: 'classic', name: 'Classico', desc: 'Design limpo e profissional', color: '#3b82f6' },
    { id: 'modern', name: 'Moderno', desc: 'Visual contemporaneo', color: '#8b5cf6' },
    { id: 'minimalist', name: 'Minimalista', desc: 'Simples e direto', color: '#6b7280' },
    { id: 'vibrant', name: 'Vibrante', desc: 'Cores vivas e energia', color: '#f59e0b' },
    { id: 'dark', name: 'Escuro', desc: 'Tema escuro sofisticado', color: '#1e293b' },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/partner/template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ template: selectedTemplate }),
      });
      await fetch(`${API_URL}/api/partner/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ site_colors: { primary: selectedColor } }),
      });
      onUpdate();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white" data-testid="site-title">Meu Site</h1>
        {data?.profile && (
          <a href={`/parceiros/${data.profile.slug}?template=${selectedTemplate}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white/80 rounded-xl hover:bg-white/20 transition-colors text-sm"
            data-testid="preview-site-btn"
          >
            <Eye className="w-4 h-4" /> Pre-visualizar
          </a>
        )}
      </div>

      {/* Colors */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold">Cores do Site</h3>
          <span className="text-white/40 text-xs">{maxColors === 999 ? 'Ilimitado' : `${maxColors} disponiveis`}</span>
        </div>
        <div className="flex gap-3 flex-wrap">
          {COLOR_PALETTE.map((c, i) => {
            const locked = i >= maxColors;
            return (
              <button key={c.id} onClick={() => !locked && setSelectedColor(c.id)}
                className={`relative w-14 h-14 rounded-xl border-2 transition-all ${locked ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:scale-105'} ${
                  selectedColor === c.id ? 'border-white ring-2 ring-white/30' : 'border-transparent'
                }`}
                style={{ background: `linear-gradient(135deg, ${c.primary}, ${c.secondary})` }}
                data-testid={`color-${c.id}`}
                disabled={locked}
              >
                {locked && <Lock className="w-4 h-4 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Templates */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold">Template Visual</h3>
          <span className="text-white/40 text-xs">{maxTemplates === 999 ? 'Ilimitado' : `${maxTemplates} disponiveis`}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t, i) => {
            const locked = i >= maxTemplates;
            return (
              <button key={t.id} onClick={() => !locked && setSelectedTemplate(t.id)}
                className={`p-5 rounded-xl border text-left transition-all ${locked ? 'opacity-40 cursor-not-allowed' : ''} ${
                  selectedTemplate === t.id ? 'border-green-500 bg-green-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
                data-testid={`template-${t.id}-btn`} disabled={locked}
              >
                <div className="w-full h-20 rounded-lg mb-3 relative" style={{ background: `linear-gradient(135deg, ${t.color}, ${t.color}80)` }}>
                  {locked && <div className="absolute inset-0 flex items-center justify-center"><Lock className="w-6 h-6 text-white" /></div>}
                </div>
                <h3 className="text-white font-semibold">{t.name}</h3>
                <p className="text-white/50 text-sm mt-1">{t.desc}</p>
                {selectedTemplate === t.id && !locked && <span className="inline-block mt-2 text-xs text-green-400 font-medium">Selecionado</span>}
                {locked && <span className="inline-block mt-2 text-xs text-amber-400 font-medium">Faca upgrade</span>}
              </button>
            );
          })}
        </div>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="px-6 py-3 rounded-xl font-bold text-white transition-all disabled:opacity-50"
        style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}
        data-testid="save-template-btn"
      >
        {saving ? 'Salvando...' : 'Salvar Configuracoes'}
      </button>
    </div>
  );
};

/* ====== PLAN TAB ====== */
const PlanTab = ({ data }) => {
  if (!data) return null;
  const { plan_type, plan } = data;

  const allPlans = [
    { id: 'amador', name: 'Parceiro Inicial', sub: 'Comece a vender', price: 'R$ 49,90/mes', products: '15', colors: '3', templates: '1' },
    { id: 'profissional', name: 'Parceiro Profissional', sub: 'Cresça seu negócio', price: 'R$ 89,90/mes', products: '30', colors: '5', templates: '3' },
    { id: 'expert', name: 'Parceiro Avançado', sub: 'Estrutura completa', price: 'R$ 129,90/mes', products: '45', colors: '8', templates: '5' },
    { id: 'ocara', name: 'Parceiro Enterprise', sub: 'Operação em grande escala', price: 'R$ 249,90/mes', products: 'Ilimitado', colors: 'Ilimitado', templates: 'Ilimitado' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6" data-testid="plan-title">Meu Plano</h1>

      {/* Current Plan */}
      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">{plan?.name}</h3>
            <p className="text-white/50 mt-1">{plan?.subtitle}</p>
          </div>
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-400" data-testid="subscription-status">
            Ativo
          </span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="bg-white/5 rounded-lg p-3 text-center">
            <p className="text-white/50 text-xs">Produtos</p>
            <p className="text-white font-bold mt-1">{plan?.max_products === -1 ? 'Ilimitado' : plan?.max_products}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-center">
            <p className="text-white/50 text-xs">Cores</p>
            <p className="text-white font-bold mt-1">{plan?.max_colors === -1 ? 'Ilimitado' : plan?.max_colors}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-center">
            <p className="text-white/50 text-xs">Templates</p>
            <p className="text-white font-bold mt-1">{plan?.max_templates === -1 ? 'Ilimitado' : plan?.max_templates}</p>
          </div>
        </div>
      </div>

      {/* All Plans */}
      <h3 className="text-lg font-semibold text-white mb-4">Todos os Planos</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {allPlans.map((p) => {
          const isCurrent = p.id === plan_type;
          return (
            <div key={p.id} className={`rounded-xl p-5 border transition-all ${isCurrent ? 'border-green-500 bg-green-500/10' : 'border-white/10 bg-white/5'}`}
              data-testid={`plan-card-${p.id}`}>
              {isCurrent && <span className="text-xs text-green-400 font-bold mb-2 block">PLANO ATUAL</span>}
              <h4 className="text-white font-bold">{p.name}</h4>
              <p className="text-white/40 text-xs mb-3">{p.sub}</p>
              <p className="text-2xl font-bold text-white mb-4">{p.price}</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-white/60"><span>Produtos</span><span className="text-white font-medium">{p.products}</span></div>
                <div className="flex justify-between text-white/60"><span>Cores</span><span className="text-white font-medium">{p.colors}</span></div>
                <div className="flex justify-between text-white/60"><span>Templates</span><span className="text-white font-medium">{p.templates}</span></div>
              </div>
              {!isCurrent && (
                <button className="w-full mt-4 py-2.5 rounded-xl font-medium text-sm border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
                  {allPlans.indexOf(p) > allPlans.findIndex(x => x.id === plan_type) ? 'Fazer Upgrade' : 'Downgrade'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-white/30 text-sm mt-6">Para alterar seu plano, entre em contato com nosso suporte.</p>
    </div>
  );
};

export default PartnerDashboardPage;
